import { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  Download,
  Save,
  Code,
  FileText,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';

type TemplateMetadata = {
  name: string;
  description: string;
  category: string;
  system_prompt: string;
};

const PLACEHOLDER_RE = /\{([a-z][a-z0-9_]*)\}/gi;

const CATEGORY_OPTIONS = [
  { value: 'proposal', label: 'Proposal' },
  { value: 'letter', label: 'Letter' },
  { value: 'summary', label: 'Summary' },
  { value: 'report', label: 'Report' },
];

const SYSTEM_PROMPT_PLACEHOLDER = `This is a VIC electricity tender proposal.

Placeholders used:
{client_name}          → Full legal entity name (e.g. "Acme Pty Ltd")
{proposal_date}        → Date prepared, Australian format (e.g. "5 March 2026")
{retailer_1_name}      → Best ranked retailer's name
{retailer_1_term}      → Contract duration (e.g. "10 months")
{retailer_1_annual_spend} → Annual cost Ex GST, numbers with commas
{cur_en}               → Current annual energy cost, Ex GST
{best_en}              → Best offer annual energy cost, Ex GST
{sav_en}               → Energy saving (cur_en minus best_en)
{pct_en}               → Energy saving %, numbers only

Rules:
- All $ amounts Ex GST, with commas, NO $ prefix
- Percentages: plain number, no % symbol
- Dates: Australian format DD Month YYYY
- Use "N/A" if data unavailable`;

export function HTMLTemplateEditorPage() {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [htmlContent, setHtmlContent] = useState('');
  const [metadata, setMetadata] = useState<TemplateMetadata>({
    name: 'Untitled Template',
    description: '',
    category: 'proposal',
    system_prompt: '',
  });
  const [detectedPlaceholders, setDetectedPlaceholders] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [showPlaceholders, setShowPlaceholders] = useState(true);

  // ── Detect {placeholder} tokens in the raw HTML string ──────────────
  const detectPlaceholders = useCallback((html: string) => {
    const found = [...html.matchAll(PLACEHOLDER_RE)].map(m => m[1]);
    setDetectedPlaceholders([...new Set(found)].sort());
  }, []);

  // ── Replace an image element inside the iframe ─────────────────────
  // Must be defined BEFORE handleIframeLoad which references it.
  const replaceImage = useCallback((imgEl: HTMLImageElement) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      // Create a local object URL for preview (swap for Supabase upload in Phase 2)
      const url = URL.createObjectURL(file);
      imgEl.src = url;
    };
    input.click();
  }, []);

  // ── Set up the iframe once it finishes loading ──────────────────────
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc) return;

    // Enable full WYSIWYG editing — clicking any text puts cursor in it
    doc.designMode = 'on';

    // Inject editing-mode styles into the iframe document
    const style = doc.createElement('style');
    style.setAttribute('data-editor', 'true');
    style.textContent = `
      /* Bright blue caret so the edit cursor is always visible */
      * { caret-color: #2563eb !important; }

      /* Subtle blue outline on the element being edited */
      *:focus {
        outline: 2px solid rgba(37, 99, 235, 0.30) !important;
        outline-offset: 2px;
      }

      /* Dashed border on images to signal they are replaceable */
      img {
        cursor: pointer !important;
        transition: outline 0.15s;
      }
      img:hover {
        outline: 2px dashed #2563eb !important;
        outline-offset: 2px;
      }
    `;
    doc.head.appendChild(style);

    // designMode intercepts single-clicks on images (for selection/resize).
    // Use DOUBLE-CLICK to replace — single-click still selects the image normally.
    doc.addEventListener('dblclick', (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        e.preventDefault();
        replaceImage(target as HTMLImageElement);
      }
    });
  }, [replaceImage]);

  // ── Get current HTML from iframe (includes any edits the user made) ─
  const getCurrentHtml = useCallback((): string => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return htmlContent;
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  }, [htmlContent]);

  // ── Import HTML from file ────────────────────────────────────────────
  const handleImportHtml = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.html,.htm';

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (ev) => {
        const raw = ev.target?.result as string;
        setHtmlContent(raw);
        detectPlaceholders(raw);

        // Auto-fill name from filename if still untitled
        setMetadata(prev =>
          prev.name === 'Untitled Template'
            ? { ...prev, name: file.name.replace(/\.(html|htm)$/i, '') }
            : prev
        );
      };
      reader.readAsText(file);
    };

    input.click();
  }, [detectPlaceholders]);

  // ── Export current HTML (with edits) ───────────────────────────────
  const handleExportHtml = useCallback(() => {
    const html = getCurrentHtml();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${metadata.name.replace(/\s+/g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [getCurrentHtml, metadata.name]);

  // ── Save template ────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveMessage(null);

    const html = getCurrentHtml();

    // TODO (Phase 2): POST to /api/scopingai/templates → save to tender_template table
    console.log('[HTMLTemplateEditor] Save payload:', {
      name: metadata.name,
      description: metadata.description,
      category: metadata.category,
      system_prompt: metadata.system_prompt,
      html_content: html,
      detected_placeholders: detectedPlaceholders,
    });

    await new Promise(r => setTimeout(r, 700));
    setIsSaving(false);
    setSaveMessage('Saved! (DB integration in Phase 2)');
    setTimeout(() => setSaveMessage(null), 4000);
  }, [getCurrentHtml, metadata, detectedPlaceholders]);

  const updateMeta = useCallback(
    (key: keyof TemplateMetadata, value: string) =>
      setMetadata(prev => ({ ...prev, [key]: value })),
    []
  );

  const hasContent = htmlContent.length > 0;

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-100">

      {/* ── Top toolbar ── */}
      <div className="h-12 bg-white border-b border-gray-200 flex items-center px-4 gap-3 flex-shrink-0 shadow-sm z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-gray-500 hover:text-gray-900 text-sm transition-colors"
        >
          <ArrowLeft size={15} />
          Back
        </button>

        <div className="w-px h-5 bg-gray-200" />

        <div className="flex items-center gap-2 min-w-0">
          <FileText size={14} className="text-blue-600 flex-shrink-0" />
          <input
            value={metadata.name}
            onChange={e => updateMeta('name', e.target.value)}
            className="text-sm font-medium text-gray-800 bg-transparent border-0 outline-none min-w-0 w-56"
            placeholder="Template name…"
          />
        </div>

        {saveMessage && (
          <span className="text-xs text-green-600 font-medium">{saveMessage}</span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleImportHtml}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 transition-colors"
          >
            <Upload size={13} />
            Import HTML
          </button>

          <button
            onClick={handleExportHtml}
            disabled={!hasContent}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={13} />
            Export HTML
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving || !hasContent}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={13} />
            {isSaving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex overflow-hidden min-h-0">

        {/* ── Left sidebar ── */}
        <div className="w-72 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Template Details
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <input
                value={metadata.name}
                onChange={e => updateMeta('name', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Proposal Template v1"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={metadata.description}
                onChange={e => updateMeta('description', e.target.value)}
                rows={2}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="VIC commercial electricity proposal"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={metadata.category}
                onChange={e => updateMeta('category', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <span className="flex items-center gap-1">
                  <Sparkles size={11} className="text-blue-500" />
                  AI System Prompt
                </span>
                <span className="text-gray-400 font-normal block mt-0.5 text-xs">
                  Explains {'{placeholders}'} to the AI during generation
                </span>
              </label>
              <textarea
                value={metadata.system_prompt}
                onChange={e => updateMeta('system_prompt', e.target.value)}
                rows={10}
                className="w-full text-xs font-mono border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none leading-relaxed"
                placeholder={SYSTEM_PROMPT_PLACEHOLDER}
              />
            </div>

            {detectedPlaceholders.length > 0 && (
              <div className="border border-blue-100 rounded-md overflow-hidden">
                <button
                  onClick={() => setShowPlaceholders(p => !p)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-blue-50 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <span>Detected Placeholders ({detectedPlaceholders.length})</span>
                  {showPlaceholders ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>

                {showPlaceholders && (
                  <div className="p-2 flex flex-wrap gap-1 bg-white">
                    {detectedPlaceholders.map(p => (
                      <span
                        key={p}
                        className="inline-block px-1.5 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200 rounded"
                      >
                        {'{' + p + '}'}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {!hasContent && (
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleImportHtml}
                className="w-full py-6 border-2 border-dashed border-gray-200 rounded-lg text-xs text-gray-400 hover:border-blue-400 hover:text-blue-500 flex flex-col items-center gap-2 transition-colors"
              >
                <Upload size={18} />
                <span className="font-medium">Import HTML to get started</span>
              </button>
            </div>
          )}
        </div>

        {/* ── Canvas area ── */}
        <div className="flex-1 relative overflow-hidden bg-gray-300">

          {hasContent ? (
            <>
              {/* Hint bar */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-gray-900/80 text-white text-xs px-3 py-1 rounded-full shadow pointer-events-none whitespace-nowrap">
                Click text to edit · Double-click images to replace
              </div>

              {/* Iframe renders the HTML exactly — designMode makes it editable */}
              <iframe
                ref={iframeRef}
                srcDoc={htmlContent}
                onLoad={handleIframeLoad}
                className="w-full h-full border-0 bg-white"
                title="Template preview"
                sandbox="allow-same-origin allow-scripts"
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-white border-2 border-dashed border-gray-300 flex items-center justify-center mb-4">
                <Code size={28} className="text-gray-300" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1">No template loaded</h3>
              <p className="text-xs text-gray-300">
                Click <strong className="text-gray-400">Import HTML</strong> in the toolbar
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
